import { type KeyboardEvent, useEffect, useLayoutEffect, useRef, useState } from 'react';
import * as RadixSlider from '@radix-ui/react-slider';
import classNames from 'classnames';

import { nextKeyValue, SLIDER_KEYS, tickPercentages } from './sliderMath';

import styles from './Slider.module.scss';

export interface SliderProps {
    value: number;
    min: number;
    max: number;
    step?: number;
    disabled?: boolean;
    /** Every value the handle passes while dragging, and each arrow-key step. */
    onChange?: (value: number) => void;
    /** The value a drag ended on, or the value a key press left the handle on. */
    onAfterChange?: (value: number) => void;
    /** Draws a tick at every step. */
    ticks?: boolean;
    /**
     * Text for a value: shown in a bubble over the handle while it is dragged, and read
     * out by screen readers in place of the bare number.
     */
    formatValue?: (value: number) => string;
    className?: string;
    'aria-label'?: string;
    'aria-labelledby'?: string;
}

/** A single-value slider on Radix, themed through the semantic tokens. */
export default function Slider(props: SliderProps) {
    const {
        value,
        min,
        max,
        step = 1,
        disabled = false,
        onChange,
        onAfterChange,
        ticks = false,
        formatValue,
        className,
        'aria-label': ariaLabel,
        'aria-labelledby': ariaLabelledBy,
    } = props;
    const [dragging, setDragging] = useState(false);
    // The value the handle last moved to, which a parent that holds `value` may not have
    // passed back yet when a release or key-up commits it.
    const latest = useRef(value);
    useLayoutEffect(() => {
        latest.current = value;
    }, [value]);

    const change = (next: number) => {
        latest.current = next;
        onChange?.(next);
    };

    // A drag ends wherever the pointer is let go, which can be well outside the slider.
    useEffect(() => {
        if (!dragging) return undefined;
        const end = () => {
            setDragging(false);
            onAfterChange?.(latest.current);
        };
        document.addEventListener('pointerup', end);
        document.addEventListener('pointercancel', end);
        return () => {
            document.removeEventListener('pointerup', end);
            document.removeEventListener('pointercancel', end);
        };
    }, [dragging, onAfterChange]);

    const handleKeyDown = (event: KeyboardEvent<HTMLSpanElement>) => {
        if (disabled) return;
        // Page keys move two steps and Shift does not enlarge a step, as before Radix.
        const next = nextKeyValue(event, latest.current, { min, max, step });
        if (next === undefined) return;
        event.preventDefault();
        if (next !== latest.current) change(next);
    };

    const handleKeyUp = (event: KeyboardEvent<HTMLSpanElement>) => {
        if (!disabled && SLIDER_KEYS.includes(event.key)) onAfterChange?.(latest.current);
    };

    const valueText = formatValue?.(value);

    return (
        <RadixSlider.Root
            className={classNames(styles.root, dragging && styles.dragging, className)}
            value={[value]}
            min={min}
            max={max}
            step={step}
            disabled={disabled}
            onValueChange={([next]) => {
                if (next !== undefined) change(next);
            }}
            onPointerDown={(event) => {
                if (!disabled && event.button === 0) setDragging(true);
            }}
            onKeyDown={handleKeyDown}
            onKeyUp={handleKeyUp}>
            <RadixSlider.Track className={styles.track}>
                <RadixSlider.Range className={styles.range} />
            </RadixSlider.Track>
            {ticks && (
                <span className={styles.ticks} aria-hidden="true">
                    {tickPercentages(min, max, step).map((percent) => (
                        <span
                            key={percent}
                            className={styles.tick}
                            style={{ left: `${percent}%` }}
                        />
                    ))}
                </span>
            )}
            <RadixSlider.Thumb
                className={styles.thumb}
                aria-label={ariaLabel}
                aria-labelledby={ariaLabelledBy}
                aria-valuetext={valueText}
                aria-disabled={disabled}>
                {dragging && valueText !== undefined && (
                    <span className={styles.tooltip} aria-hidden="true">
                        {valueText}
                    </span>
                )}
            </RadixSlider.Thumb>
        </RadixSlider.Root>
    );
}
